import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import {
  PromotionPayload,
  PromotionPool,
  PromotionState,
  useDeactivatePromotionMutation,
  useExportPromotionCodesMutation,
  useGetPromotionQuery,
  useReactivatePromotionMutation,
} from 'api/promotion'
import { getErrorMessage } from 'api/utils'
import Badge from 'components/Badge'
import ModalContainer from 'components/ModalContainer'
import PageHeader from 'components/PageHeader'
import Section from 'components/Section'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import TabPane from 'components/Tabs/TabPane'
import palette from 'constants/palette'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import { useUrlFileDownload } from 'hooks/useUrlFileDownload'
import { buildTranslate } from 'locales'
import moment from 'moment-timezone'
import React, { useEffect, useState } from 'react'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { Link, RouteComponentProps } from 'react-router-dom'
import { push } from 'react-router-redux'
import { errorHandler } from 'utilities/errorHandler'
import { useUserTimeZone } from 'utilities/userTimeZone'
import ChannelStatusCell from '../ChannelStatusCell'
import { onlineAcceptance, posAcceptance } from '../compatibility'
import PosRedemptionBanner from '../PosRedemptionBanner'
import '../PromoCodes.scss'
import { areCodesStillGenerating, isCsvGenerating } from '../promotionPool'

const t = buildTranslate('promo_codes.detail')

// Reward summary copy for the Detail page. Mirrors the rewards UI labels
// so a merchant who configured the reward in the rewards interface sees
// consistent wording here.
//
// `value` is `discountValue` (number | null). For amount/percent/fixed_price
// it should be present, but the type allows null — fall back to the
// type-only copy if it's missing rather than rendering raw API enums.
function formatRewardSummary(promotion: PromotionPayload): string {
  const value = promotion.discountValue
  const tSummary = (key: string, params?: Record<string, unknown>) =>
    t(`configuration.rewards.summary.${key}`, params)

  switch (promotion.discountType) {
    case 'percent':
      return value != null
        ? tSummary('percent', { value })
        : tSummary('percent_unknown')
    case 'amount':
      return value != null
        ? tSummary('amount', { value })
        : tSummary('amount_unknown')
    case 'item':
      return tSummary('item')
    case 'fixed_price':
      // Free-discount scopes (model invariant: price_cap = 0) match the
      // `free_*` prefix per Program::Redeem::Manual::FixedPrice::Template::
      // FREE_DISCOUNT_SCOPES. Convention used elsewhere in the codebase
      // (RedeemManager, CampaignCenter incentive builder).
      if (promotion.discountScope?.startsWith('free_')) {
        return tSummary('fixed_price_free')
      }
      return value != null
        ? tSummary('fixed_price_paid', { value })
        : tSummary('fixed_price_unknown')
    default:
      // Defensive: TS guarantees exhaustive `discountType`, but if a new
      // type is added in the API and not yet handled here, render a
      // translated placeholder rather than the raw enum string.
      return tSummary('unknown')
  }
}

type RouteParams = {
  id: string
}

enum Tabs {
  Configuration = 'configuration',
}

function buildDateDescription(
  startsAt: string | null,
  endsAt: string | null,
  formatFn: (dateStr: string) => string
): string {
  if (startsAt) {
    const end = endsAt ? formatFn(endsAt) : t('ongoing')
    return `${formatFn(startsAt)} - ${end}`
  }
  if (endsAt) {
    return `${t('until_date')} ${formatFn(endsAt)}`
  }
  return t('ongoing')
}

const STATUS_COLORS: Record<PromotionState, string> = {
  active: 'nephritis-20-bkg nephritis-50',
  draft: 'grey-10-bkg grey-70',
  inactive: 'grey-10-bkg grey-70',
}

const Detail: React.FC<RouteComponentProps<RouteParams>> = ({ match }) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const promotionId = match.params.id
  const [activeTab, setActiveTab] = useState<string>(Tabs.Configuration)
  const [showEndModal, setShowEndModal] = useState(false)
  const [showReactivateModal, setShowReactivateModal] = useState(false)
  const userTimeZone = useUserTimeZone()
  const reactivateEnabled = useFlag('mx-promo-code-reactivate', false)
  // Channel-acceptance entry (LOY-622) rides the same flag as the Overview tab.
  const showChannels = useFlag('mx-promo-codes-overview', false)
  const toastInstoreEnabled = useFlag('toast-promo-instore-beta', false)
  const merchant = useCurrentMerchant()

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return t('configuration.redemption.no_date')
    return moment.tz(dateStr, userTimeZone).format('M/D/YYYY, h:mm A z')
  }

  // Poll every 10s while the CSV is mid-generation so merchants see the
  // Download CSV button appear without having to refresh. Polling stops as
  // soon as the pool settles (URL present, or state is null/failed/complete),
  // so idle detail pages don't hammer the API.
  //
  // `pollingInterval` is carried in local state and updated via `setState`
  // during render when the derived target differs. We can't compute it
  // purely inline because the value depends on the query's own result
  // (temporal dead zone). React bails on equal-value setState so this is
  // a no-op on steady renders.
  const [pollingInterval, setPollingInterval] = useState(0)
  const { data, isLoading } = useGetPromotionQuery(promotionId, {
    pollingInterval,
  })
  const [deactivatePromotion, { isLoading: isEnding }] =
    useDeactivatePromotionMutation()
  const [reactivatePromotion, { isLoading: isReactivating }] =
    useReactivatePromotionMutation()
  const [exportCodes, { isLoading: isExporting }] =
    useExportPromotionCodesMutation()
  const {
    downloadUrlFile,
    isDownloading,
    error: downloadError,
  } = useUrlFileDownload()

  const promotion = data?.promotion

  const poolIsGenerating = promotion?.pool
    ? isCsvGenerating(promotion.pool)
    : false
  const nextPollingInterval = poolIsGenerating ? 10000 : 0
  if (nextPollingInterval !== pollingInterval) {
    setPollingInterval(nextPollingInterval)
  }

  // Surface download failures as a toast. `useUrlFileDownload` swallows the
  // underlying error into its `error` state, so a try/catch around the
  // await wouldn't fire — the hook is shared with CCPACard/PrivacyPolicyCard
  // which rely on this contract.
  useEffect(() => {
    if (!downloadError) return
    errorHandler.capture(downloadError, {
      promotionId: match.params.id,
      action: 'download_csv',
    })
    dispatch(alert({ key: 'danger', message: t('alerts.download_error') }))
  }, [downloadError, dispatch, match.params.id])

  // Warn about unknown venue types so new values don't silently fall back to 'all'.
  // Effect (not render-path) so we don't fire repeatedly on re-renders.
  const venue = promotion?.redemptionVenue
  useEffect(() => {
    if (venue && !['all', 'instore', 'online'].includes(venue)) {
      errorHandler.capture(`Unknown redemptionVenue: ${venue}`, {
        promotionId: promotion?.id,
        redemptionVenue: venue,
      })
    }
  }, [venue, promotion?.id])

  const handleDownloadCsv = async (url: string) => {
    // Intentionally unawaited-errors: `useUrlFileDownload` swallows errors
    // into its `error` state, which the effect above turns into a toast.
    await downloadUrlFile(url, `${promotion?.name}-codes.csv`)
  }

  const handleExportCsv = async () => {
    const result = await exportCodes({ promotionId })
    if ('error' in result) {
      const message = getErrorMessage(result.error, t('alerts.export_error'))
      dispatch(alert({ key: 'danger', message }))
      return
    }
    dispatch(
      alert({ key: 'success', message: t('alerts.export_queued'), timeout: 5 })
    )
  }

  const handleEnd = async () => {
    try {
      const result = await deactivatePromotion(promotionId)
      if ('error' in result) {
        const message = getErrorMessage(result.error, t('alerts.end_error'))
        dispatch(alert({ key: 'danger', message }))
        return
      }
      dispatch(alert({ key: 'success', message: t('alerts.end_success') }))
      dispatch(push('/promo_codes'))
    } finally {
      setShowEndModal(false)
    }
  }

  const handleReactivate = async () => {
    try {
      const result = await reactivatePromotion(promotionId)
      if ('error' in result) {
        const message = getErrorMessage(
          result.error,
          t('alerts.reactivate_error')
        )
        dispatch(alert({ key: 'danger', message }))
        return
      }
      dispatch(
        alert({ key: 'success', message: t('alerts.reactivate_success') })
      )
    } finally {
      setShowReactivateModal(false)
    }
  }

  // Bail only when we have no promotion to show — first-load failure or the
  // promotion was deleted mid-session. A polled 5xx on a page that already
  // rendered keeps `data` cached in RTK Query, so `promotion` stays set and
  // we don't bounce the merchant to Landing over a transient backend blip.
  const shouldRedirect = !isLoading && !promotion
  useEffect(() => {
    if (!shouldRedirect) return
    dispatch(push('/promo_codes'))
    dispatch(alert({ key: 'danger', message: t('alerts.load_error') }))
  }, [shouldRedirect, dispatch])

  if (isLoading || shouldRedirect || !promotion) {
    return null
  }

  const pool = promotion.pool
  const isUniqueCodes = pool?.generationType === 'single_use'
  const staticCode = pool?.staticCode

  // CSV button: only meaningful for unique-code pools. URL-driven:
  // - If a presigned download URL is present, show "Download CSV" (terminal state).
  // - Else if the pool is mid-generation (any state besides 'complete'), show
  //   "Generating CSV..." disabled.
  // - Else (no URL and no in-progress state) fall back to the async export
  //   endpoint so the merchant can manually (re)queue generation.
  const renderCsvButton = (csvPool: PromotionPool) => {
    const csvUrl = csvPool.codesCsvUrl
    if (csvUrl) {
      return (
        <Button
          kind="secondary"
          onClick={() => handleDownloadCsv(csvUrl)}
          disabled={isDownloading}
        >
          {isDownloading
            ? t('actions.downloading_csv')
            : t('actions.download_csv')}
        </Button>
      )
    }
    if (isCsvGenerating(csvPool)) {
      // Compact disabled button with spinner icon. The "several minutes / refresh"
      // context lives beside the "Generating…" indicator on the body card, not
      // in the header — keeps the header tight and next to Edit / End promotion.
      return (
        <Button kind="secondary" disabled>
          <span className="fa fa-spin fa-circle-o-notch mr-xs" />
          {t('actions.generating_csv')}
        </Button>
      )
    }
    return (
      <Button kind="secondary" onClick={handleExportCsv} disabled={isExporting}>
        {isExporting ? t('actions.exporting_csv') : t('actions.export_csv')}
      </Button>
    )
  }

  const csvButton = isUniqueCodes && pool ? renderCsvButton(pool) : null

  const editButton = (
    <Button
      kind="secondary"
      onClick={() => dispatch(push(`/promo_codes/${promotionId}/edit`))}
    >
      {t('actions.edit')}
    </Button>
  )

  const renderLifecycleButtons = () => {
    if (promotion.state === 'inactive') {
      if (!reactivateEnabled) return null

      return (
        <>
          {editButton}
          <Button
            onClick={() => setShowReactivateModal(true)}
            disabled={isReactivating}
          >
            {isReactivating
              ? t('actions.reactivating')
              : t('actions.reactivate_promotion')}
          </Button>
        </>
      )
    }

    return (
      <>
        {editButton}
        {promotion.state === 'active' && (
          <Button onClick={() => setShowEndModal(true)}>
            {t('actions.end_promotion')}
          </Button>
        )}
      </>
    )
  }

  const lifecycleButtons = renderLifecycleButtons()

  const formatShortDate = (dateStr: string) =>
    moment.tz(dateStr, userTimeZone).format('MMM D, YYYY')

  const dateDescription = buildDateDescription(
    promotion.startsAt,
    promotion.endsAt,
    formatShortDate
  )

  const statusLabel = t(`status.${promotion.state}`) || promotion.state

  return (
    <>
      <Helmet>
        <title>{promotion.name}</title>
      </Helmet>
      <PageHeader
        title={promotion.name}
        description={dateDescription}
        backPath="/promo_codes"
        topComponent={
          <Badge
            className={STATUS_COLORS[promotion.state]}
            value={statusLabel}
          />
        }
        menu={
          csvButton || lifecycleButtons ? (
            <div className="promo-btn-group">
              {csvButton}
              {lifecycleButtons}
            </div>
          ) : undefined
        }
      >
        <Tab.Container
          activeKey={activeTab}
          transition={false}
          onSelect={(key: string | null) => key && setActiveTab(key)}
        >
          <TabNav>
            <TabItem
              state={Tabs.Configuration}
              title={t('tabs.configuration')}
            />
          </TabNav>
          <Tab.Content className="pb-l h-100 grey-05-bkg">
            <TabPane state={Tabs.Configuration}>
              <PosRedemptionBanner className="mb-l" />
              <Text.Header3 className="mb-l fst-italic">
                {t('configuration.title')}
              </Text.Header3>

              {/* Promo code card */}
              <Section className="promo-detail-card mb-m">
                <Text.Header4 className="mb-l">
                  {t('configuration.promo_code.title')}
                </Text.Header4>
                <div className="promo-detail-field">
                  <Text.BodyText4 bold>
                    {t('configuration.promo_code.type_label')}
                  </Text.BodyText4>
                  <Text.BodyText3 className="grey-70">
                    {isUniqueCodes
                      ? t('configuration.promo_code.type_unique')
                      : t('configuration.promo_code.type_single')}
                  </Text.BodyText3>
                </div>
                {isUniqueCodes && pool && (
                  <div className="promo-detail-field">
                    <Text.BodyText4 bold>
                      {t('configuration.promo_code.count_label')}
                    </Text.BodyText4>
                    <Text.BodyText3 className="grey-70">
                      {areCodesStillGenerating(pool) ? (
                        <>
                          <span className="fa fa-spin fa-circle-o-notch mr-xs" />
                          {t('configuration.promo_code.count_generating')}
                        </>
                      ) : (
                        pool.totalCodes.toLocaleString()
                      )}
                    </Text.BodyText3>
                    {isCsvGenerating(pool) && (
                      <Text.BodyText5 className="grey-50 fst-italic mt-xs d-block">
                        {t('actions.generating_csv_helper')}
                      </Text.BodyText5>
                    )}
                  </div>
                )}
                {staticCode && (
                  <div className="promo-detail-field">
                    <Text.BodyText4 bold>
                      {t('configuration.promo_code.code_label')}
                    </Text.BodyText4>
                    <Text.BodyText3 className="grey-70">
                      {staticCode}
                    </Text.BodyText3>
                  </div>
                )}
              </Section>

              {/* Rewards card */}
              <Section className="promo-detail-card mb-m">
                <Text.Header4 className="mb-l">
                  {t('configuration.rewards.title')}
                </Text.Header4>
                <div className="promo-detail-field">
                  <Text.BodyText4 bold>
                    {t('configuration.rewards.prompt')}
                  </Text.BodyText4>
                  {promotion.redeemTemplateId !== null ? (
                    <Link
                      to={`/rewards/${promotion.redeemTemplateId}`}
                      className={css({ color: palette.belizeHole })}
                    >
                      {formatRewardSummary(promotion)}{' '}
                      <i className="fa fa-external-link" aria-hidden="true" />
                    </Link>
                  ) : (
                    <Text.BodyText3 className="grey-70">
                      {formatRewardSummary(promotion)}
                    </Text.BodyText3>
                  )}
                </div>
              </Section>

              {/* Locations / Venue card */}
              {promotion.redemptionVenue && (
                <Section className="promo-detail-card mb-m">
                  <Text.Header4 className="mb-l">
                    {t('configuration.locations.title')}
                  </Text.Header4>
                  <div className="promo-detail-field">
                    <Text.BodyText4 bold>
                      {t('configuration.locations.prompt')}
                    </Text.BodyText4>
                    <Text.BodyText3 className="grey-70">
                      {['all', 'instore', 'online'].includes(
                        promotion.redemptionVenue
                      )
                        ? t(
                            `configuration.locations.${promotion.redemptionVenue}`
                          )
                        : t('configuration.locations.all')}
                    </Text.BodyText3>
                  </div>
                </Section>
              )}

              {/* Channel availability card (LOY-622) */}
              {showChannels && (
                <Section className="promo-detail-card mb-m">
                  <Text.Header4 className="mb-l">
                    {t('configuration.channels.title')}
                  </Text.Header4>
                  <div className="promo-detail-field">
                    <Text.BodyText4 bold>
                      {t('configuration.channels.online_label')}
                    </Text.BodyText4>
                    <ChannelStatusCell
                      channel="online"
                      state={onlineAcceptance(
                        merchant,
                        promotion.redemptionVenue
                      )}
                    />
                  </div>
                  <div className="promo-detail-field">
                    <Text.BodyText4 bold>
                      {t('configuration.channels.pos_label')}
                    </Text.BodyText4>
                    <ChannelStatusCell
                      channel="pos"
                      state={posAcceptance(
                        merchant,
                        toastInstoreEnabled,
                        promotion.redemptionVenue
                      )}
                    />
                  </div>
                </Section>
              )}

              {/* Redemption settings card */}
              <Section className="promo-detail-card">
                <Text.Header4 className="mb-l">
                  {t('configuration.redemption.title')}
                </Text.Header4>
                <div className="promo-detail-field">
                  <Text.BodyText4 bold>
                    {t('configuration.redemption.valid_from')}
                  </Text.BodyText4>
                  <Text.BodyText3 className="grey-70">
                    {formatDate(promotion.startsAt)}
                  </Text.BodyText3>
                </div>
                <div className="promo-detail-field">
                  <Text.BodyText4 bold>
                    {t('configuration.redemption.valid_until')}
                  </Text.BodyText4>
                  <Text.BodyText3 className="grey-70">
                    {formatDate(promotion.endsAt)}
                  </Text.BodyText3>
                </div>
                {((promotion.maximumDiscount !== null &&
                  promotion.maximumDiscount > 0) ||
                  (promotion.minimumSpend !== null &&
                    promotion.minimumSpend > 0)) && (
                  <div className="promo-detail-field">
                    <Text.BodyText4 bold className="mb-s">
                      {t('configuration.redemption.restrictions')}
                    </Text.BodyText4>
                    {promotion.maximumDiscount !== null &&
                      promotion.maximumDiscount > 0 && (
                        <div className="promo-detail-restriction">
                          <i
                            className="fa fa-check promo-detail-check-icon"
                            aria-hidden="true"
                          />
                          <Text.BodyText4 className="grey-70">
                            {t('configuration.redemption.max_discount', {
                              amount: promotion.maximumDiscount.toFixed(2),
                            })}
                          </Text.BodyText4>
                        </div>
                      )}
                    {promotion.minimumSpend !== null &&
                      promotion.minimumSpend > 0 && (
                        <div className="promo-detail-restriction">
                          <i
                            className="fa fa-check promo-detail-check-icon"
                            aria-hidden="true"
                          />
                          <Text.BodyText4 className="grey-70">
                            {t('configuration.redemption.min_spend', {
                              amount: promotion.minimumSpend.toFixed(2),
                            })}
                          </Text.BodyText4>
                        </div>
                      )}
                  </div>
                )}
              </Section>
            </TabPane>
          </Tab.Content>
        </Tab.Container>
      </PageHeader>

      <ModalContainer
        isOpen={showEndModal}
        onClose={() => setShowEndModal(false)}
        title={t('confirm_end.title')}
        proceedText={
          isEnding ? t('confirm_end.ending') : t('confirm_end.confirm')
        }
        onProceed={handleEnd}
        isLoading={isEnding}
      >
        <Text.BodyText3>{t('confirm_end.message')}</Text.BodyText3>
      </ModalContainer>

      <ModalContainer
        isOpen={showReactivateModal}
        onClose={() => setShowReactivateModal(false)}
        title={t('confirm_reactivate.title')}
        proceedText={
          isReactivating
            ? t('confirm_reactivate.reactivating')
            : t('confirm_reactivate.confirm')
        }
        onProceed={handleReactivate}
        isLoading={isReactivating}
      >
        <Text.BodyText3>{t('confirm_reactivate.message')}</Text.BodyText3>
      </ModalContainer>
    </>
  )
}

export default Detail
